import React, { forwardRef } from 'react'
import { css } from '@emotion/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import { Link } from '@fs/zion-router'
import useIsMobile from '../hooks/useIsMobile'

const pillCss = css`
  --black: black;
  --white: white;
  --hover: #fff3;
  --active: #fff4;

  &[inverted] {
    --black: white;
    --white: black;
    --hover: #0001;
    --active: #0002;
  }

  border: unset;
  margin: 0;
  box-sizing: border-box;
  min-height: 44px;
  border-radius: 22px;
  border: solid 1px var(--white);
  padding: 0 24px;
  color: var(--white);
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
  min-width: 0;
  display: flex;
  align-items: center;

  a& {
    display: inline-flex;
  }

  &[min-width] {
    min-width: 200px;
  }

  &[emphasis='special'] {
    background: #fc4e6d;
    border-color: #fc4e6d;
    &:not([disabled]) {
      color: var(--black) !important; // When it's a Link tag, it tries to overwrite the color
      &:hover {
        filter: brightness(0.8);
      }
      &:active {
        filter: brightness(0.9);
      }
    }
  }
  &[emphasis='high'] {
    background: var(--white);
    border-color: var(--white);
    &:not([disabled]) {
      color: var(--black) !important; // When it's a Link tag, it tries to overwrite the color
      &:hover {
        filter: brightness(0.8);
      }
      &:active {
        filter: brightness(0.9);
      }
    }
  }
  &[emphasis='medium'] {
    background: transparent;
    border-color: var(--white);
    &:not([disabled]) {
      color: var(--white) !important; // When it's a Link tag, it tries to overwrite the color
      &:hover {
        background: var(--hover);
      }
      &:active {
        background: var(--active);
      }
    }
  }

  &[emphasis='low'] {
    background: transparent;
    border-color: transparent;
    &:not([disabled]) {
      color: var(--white) !important; // When it's a Link tag, it tries to overwrite the color
      &:hover {
        background: var(--hover);
      }
      &:active {
        background: var(--active);
      }
    }
  }
  &[bigness='small'] {
    font-size: 12px;
    padding: 0 14px;
    min-height: 36px;
  }
  &[inverted][color='blue'] {
    background-color: #bbecf6;
    color: #01496e !important;
  }

  &[disabled],
  &[disabled]:hover {
    background: var(--background-disabled);
    color: var(--text-disabled);
    border-color: var(--pb-disabled-border-color, var(--text-disabled));
    cursor: not-allowed;
  }

  &[no-border] {
    border: none;
    font-weight: 400;
  }

  /* This is to disable hidden overflow on the icon so we can position is correctly */
  & div[data-shape] {
    margin-right: var(--pb-icon-margin-right, 8px);
    margin-left: var(--pb-icon-margin-left, -8px);
  }
`

const PButton = forwardRef((props, ref) => <button type="button" ref={ref} {...props} />)

function PillButton(
  {
    fitContent,
    minWidth,
    inverted,
    noBorder,
    to,
    children,
    Icon,
    emphasis = 'high',
    size,
    color,
    hideLabelOnMobile,
    ...props
  },
  ref
) {
  const isMobile = useIsMobile()
  const TagName = to ? Link : PButton

  if (to) {
    props.noLinkStyles = true
  }

  return (
    <TagName
      to={to}
      {...props}
      css={pillCss}
      ref={ref}
      bigness={size}
      min-width={boolAttr(minWidth)}
      no-border={boolAttr(noBorder)}
      inverted={boolAttr(inverted)}
      color={color}
      emphasis={emphasis}
      className="pill-button"
    >
      {Icon && <Icon size="sm" />}
      {!(isMobile && hideLabelOnMobile) && children}
    </TagName>
  )
}

export default forwardRef(PillButton)
